import {
  Button,
  Col,
  Collapse,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography
} from 'antd';
import { Content } from 'antd/es/layout/layout';
import CodeSampleTabs from '../codeSamples/tabs/CodeSampleTabs';
import { CodeSamples } from '../codeSamples';
import { CodeType } from '../codeSamples';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { User } from '../../interfaces/api/Auth';
import { Auth } from 'aws-amplify';
import NotificationAPIClient from 'notificationapi-js-client-sdk';
import {
  MarkAsReadModes,
  PopupPosition
} from 'notificationapi-js-client-sdk/lib/interfaces';
import { faker } from '@faker-js/faker';
import Link from 'antd/es/typography/Link';
import styled from 'styled-components';

const Text = Typography.Text;

const CustomDiv = styled.div`
  & .notificationapi-preferences-button {
    display: none;
  }
`;

const generateFakeNotifications = (
  count: number,
  params?: {
    seen?: boolean;
    date?: string;
  }
) => {
  const result = [];
  for (let index = 0; index < count; index++) {
    result.push({
      id: (new Date().getTime() + index).toString(),
      seen: params?.seen ?? faker.datatype.boolean(),
      title: `${faker.person.firstName()} reviewed ${faker.commerce.productName()}`,
      redirectURL: '#',
      imageURL: faker.image.avatar(),
      date: params?.date ?? faker.date.past().toISOString()
    });
  }
  return result;
};

const InAppContainer = (): JSX.Element => {
  const accountId = useParams<{ accountId: string }>().accountId;
  const [user, setUser] = useState<User>();
  const [popupPosition, setPopupPosition] = useState<PopupPosition>(
    PopupPosition.BottomRight
  );
  const [inline, setInline] = useState<boolean>(false);
  const [markAsReadMode, setMarkAsReadMode] = useState<MarkAsReadModes>(
    MarkAsReadModes.AUTOMATIC
  );
  const [paginated, setPaginated] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(5);
  const [mockNotifications, setMockNotifications] = useState<
    {
      id: string;
      seen: boolean;
      title: string;
      redirectURL: string;
      imageURL: string;
      date: string;
    }[]
  >([]);

  useEffect(() => {
    Auth.currentUserPoolUser().then((user: User) => {
      setUser(user);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    const notificationapi = new NotificationAPIClient({
      clientId: 'test',
      userId: 'test',
      websocket: false
    });

    notificationapi.showInApp({
      root: 'playground-inapp-widget',
      popupPosition,
      inline,
      markAsReadMode,
      pageSize,
      paginated
    });

    notificationapi.websocketHandlers.notifications({
      payload: {
        notifications: mockNotifications
      },
      route: 'inapp_web/notifications'
    });

    notificationapi.websocketHandlers.unreadCount({
      payload: {
        count: mockNotifications.filter((n) => !n.seen).length
      },
      route: 'inapp_web/unread_count'
    });
  }, [
    accountId,
    user,
    popupPosition,
    inline,
    markAsReadMode,
    paginated,
    pageSize,
    mockNotifications
  ]);

  return (
    <Content style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Collapse defaultActiveKey={['1']}>
            <Collapse.Panel
              header={
                <>
                  <Text>Playground</Text>
                  <Text code style={{ position: 'absolute', right: 12 }}>
                    notificationapi-js-client-sdk: 4.4.0
                  </Text>
                </>
              }
              key="1"
            >
              <Row>
                <Col span={12}>
                  <Space direction="vertical" size="large">
                    <Space>
                      <div style={{ width: 116 }}>
                        <Text>Popup vs. Inline</Text>
                      </div>
                      <Switch
                        checked={inline}
                        onChange={() => setInline(!inline)}
                      />
                    </Space>
                    <Space>
                      <div style={{ width: 116 }}>
                        <Text>Popup Position</Text>
                      </div>
                      <Select
                        value={popupPosition}
                        style={{ width: 180 }}
                        onChange={(value) => setPopupPosition(value)}
                        options={Object.values(PopupPosition).map((value) => ({
                          label: value,
                          value
                        }))}
                        disabled={inline}
                      />
                    </Space>
                    <Space>
                      <div style={{ width: 116 }}>
                        <Text>Mark as Read</Text>
                      </div>
                      <Select
                        value={markAsReadMode}
                        style={{ width: 180 }}
                        onChange={(value) => setMarkAsReadMode(value)}
                        options={Object.values(MarkAsReadModes).map(
                          (value) => ({
                            label: value,
                            value
                          })
                        )}
                      />
                    </Space>
                    <Space>
                      <div style={{ width: 116 }}>
                        <Text>Paginate</Text>
                      </div>
                      <Switch
                        checked={paginated}
                        onChange={() => setPaginated(!paginated)}
                      />
                    </Space>
                    <Space>
                      <div style={{ width: 116 }}>
                        <Text>Page Size</Text>
                      </div>
                      <InputNumber
                        value={pageSize}
                        style={{ width: 180 }}
                        onChange={(value) => {
                          setPageSize(value as number);
                        }}
                        disabled={!paginated}
                      />
                    </Space>

                    <Space>
                      <Button
                        type="primary"
                        onClick={() => {
                          setMockNotifications(
                            mockNotifications.concat(
                              generateFakeNotifications(1, {
                                seen: false,
                                date: new Date().toISOString()
                              })
                            )
                          );
                        }}
                      >
                        Send a mock notification
                      </Button>
                      <Button
                        onClick={() => {
                          setMockNotifications([]);
                        }}
                      >
                        Empty
                      </Button>
                      <Link
                        href="https://docs.notificationapi.com/components/inapp"
                        target="_blank"
                      >
                        Learn more
                      </Link>
                    </Space>
                  </Space>
                </Col>

                <Col span={12}>
                  <CustomDiv id="playground-inapp-widget"></CustomDiv>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
          <Collapse defaultActiveKey={['1']} style={{ marginTop: 20 }}>
            <Collapse.Panel header="Integration (Front-End)" key="1">
              <CodeSampleTabs
                mergeTags={{}}
                codeSamples={CodeSamples.filter(
                  (c) => c.codeType === CodeType.FRONTEND
                )}
                environment={{
                  clientId: accountId
                }}
                user={{
                  id: user?.attributes.email ?? ''
                }}
                notification={{
                  notificationId: 'notificationId',
                  channels: ['EMAIL'],
                  title: 'title',
                  enabled: true,
                  envId: 'envId'
                }}
              />
              <Link
                href="https://docs.notificationapi.com/reference/js-client"
                target="_blank"
              >
                Documentation
              </Link>
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>
    </Content>
  );
};

export default InAppContainer;
